import { useCallback, useEffect, useRef, useState } from 'react'

import { Wizard } from '@factorialco/deprecated-design-system'
import {
  HasEmployeesToCreateRequestsDocument,
  TrainingsTrainingRequestStatusEnum,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useWizardSteps } from './WizardSteps/useWizardSteps'
import type { WizardConfig, WizardData } from './types'

interface ActionProps {
  onClose: () => void
  onSubmit: (
    status?: TrainingsTrainingRequestStatusEnum,
    data?: WizardData,
    isDraft?: boolean
  ) => Promise<void>
}

interface Props {
  wizardConfig?: WizardConfig
  actions: ActionProps
  /**
   * The employee we are requesting the training for.
   */
  employeeId?: number
}

export const WizardComponent = ({
  wizardConfig = { fromCatalog: false, trainingId: undefined, allowDraft: true },
  actions: { onClose, onSubmit },
  employeeId,
}: Props) => {
  const { employee } = useCurrentSession()
  const targetEmployeeId = employeeId ?? employee?.id
  const {
    fromCatalog = false,
    trainingId,
    allowEmployeePayment,
    allowDraft = true,
  } = wizardConfig || {}
  const amplitudeEvent = fromCatalog
    ? trackingEvents.training.trainingRequest.addTrainingRequest.fromCatalog
    : trackingEvents.training.trainingRequest.addTrainingRequest.newProposal

  const initialData: WizardData = {
    status: 'review',
    subsidized: false,
    employeesSelection: { type: 'manual', ids: targetEmployeeId ? [targetEmployeeId] : [] },
    competencyIds: [],
    trainingId,
    paymentInstallments: allowEmployeePayment ? 1 : undefined,
  }

  // so there's this bug that for some reason, the wizard when is shown without loading anything before it does not show at all...
  // I can't figure out why, and happened since the begining, I think it has to do something with the entrance animation
  //  which I think is not working at all. Having it now separated and bundled outside the code makes it even harder to debug
  const { data: employeesData, loading } = useQuery(HasEmployeesToCreateRequestsDocument, {
    fetchPolicy: 'network-only',
  })

  const [wizardData, setWizardData] = useState<WizardData>(initialData)

  const handleSubmit = useCallback(
    async (
      status: TrainingsTrainingRequestStatusEnum = 'review',
      data: WizardData = wizardData,
      isDraft?: boolean
    ) => onSubmit(status, data, isDraft),
    [onSubmit, wizardData]
  )

  const handleOnDataChanged = (newData: WizardData) => {
    setWizardData((prev) => ({ ...prev, ...newData }))
  }

  const draftClickedRef = useRef(false)
  const draftLabel = i18n.t('trainings.training_requests.create.save_as_draft')

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target
      if (!(target instanceof HTMLElement)) return
      const button = target.closest('button')
      if (button && button.textContent?.trim() === draftLabel) {
        draftClickedRef.current = true
      }
    }

    document.addEventListener('click', handleClick, true)
    return () => document.removeEventListener('click', handleClick, true)
  }, [draftLabel])

  const handleDraftSubmit = useCallback(
    async (data: WizardData) => {
      if (!draftClickedRef.current) {
        return
      }
      draftClickedRef.current = false
      return handleSubmit('draft', data, true)
    },
    [handleSubmit]
  )
  const steps = useWizardSteps(
    {
      fromCatalog,
      trainingId,
      allowDraft,
      allowEmployeePayment,
    },
    !employeeId && Number(employeesData?.employees.employeeNamesConnection?.totalCount || 0) > 0,
    handleDraftSubmit,
    amplitudeEvent
  )

  if (loading) {
    return <Loading />
  }

  return (
    <Wizard.Modal
      title={i18n.t('trainings.training_requests.create.training_request_title')}
      icon="document-folder-board-edit"
      initialData={wizardData}
      steps={steps}
      onSubmit={() => handleSubmit()}
      onClose={onClose}
      onDataChanged={handleOnDataChanged}
    />
  )
}
